import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Users, Crown, Gift, Twitter, Linkedin, Mail, Trophy, Zap } from "lucide-react";

const MILESTONES = [
  { count: 5, bonus: "+2 extra months" },
  { count: 10, bonus: "+5 extra months" },
  { count: 25, bonus: "+12 extra months" },
];

export default function Referrals() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/referrals").then(r => setData(r.data));
    api.get("/referrals/leaderboard").then(r => setBoard(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-10">Loading...</div>;
  const url = data.referral_url;
  const shareText = "I'm using Career OS to run my career like an operating system — salary intel, AI advisor, roadmap. Try it:";
  const converted = data.converted_to_pro || 0;
  const nextMilestone = MILESTONES.find(m => m.count > converted);

  return (
    <div className="p-10 max-w-5xl" data-testid="referrals-page">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 16</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">Referrals</h1>
        <p className="text-muted-foreground mt-2 text-sm">Invite friends. Every one who upgrades to Pro earns you a free month — with milestone bonuses.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-border border border-border mb-8" data-testid="referral-stats">
        <Stat label="Free months banked" value={data.credits_months} accent icon={Gift}/>
        <Stat label="Referred" value={data.total_referred} icon={Users}/>
        <Stat label="Upgraded to Pro" value={converted} icon={Crown}/>
      </div>

      {nextMilestone && (
        <div className="card-flat p-5 mb-6 border-l-2 border-l-amber-500" data-testid="milestone-progress">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-400 mb-2"><Zap size={12}/> Next milestone</div>
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold">Reach <b className="text-amber-400">{nextMilestone.count}</b> Pro conversions for <b className="text-emerald-400">{nextMilestone.bonus}</b></div>
            <div className="font-mono text-sm text-muted-foreground">{converted} / {nextMilestone.count}</div>
          </div>
          <div className="h-1.5 bg-secondary"><div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (converted/nextMilestone.count)*100)}%` }}/></div>
        </div>
      )}

      <div className="card-flat p-6 mb-8" data-testid="referral-link-block">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Your unique link</div>
        <div className="flex items-stretch gap-2 mb-4">
          <input data-testid="referral-url" readOnly value={url} className="flex-1 bg-card border border-border px-4 py-3 font-mono text-sm text-zinc-200"/>
          <button data-testid="referral-copy" onClick={()=>{navigator.clipboard.writeText(url); toast.success("Copied");}} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-4 flex items-center gap-2"><Copy size={14}/> Copy</button>
        </div>
        <div className="flex flex-wrap gap-2">
          <a data-testid="share-twitter" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-3 py-2 text-xs flex items-center gap-2"><Twitter size={12}/> X / Twitter</a>
          <a data-testid="share-linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-3 py-2 text-xs flex items-center gap-2"><Linkedin size={12}/> LinkedIn</a>
          <a data-testid="share-email" href={`mailto:?subject=${encodeURIComponent("You need Career OS")}&body=${encodeURIComponent(shareText + "\n\n" + url)}`} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-3 py-2 text-xs flex items-center gap-2"><Mail size={12}/> Email</a>
        </div>
      </div>

      <div className="card-flat p-6 mb-8">
        <div className="font-display font-bold text-xl mb-4">How it works</div>
        <ol className="space-y-3 text-sm text-foreground/80">
          <li className="flex gap-3"><span className="font-mono text-primary font-bold">01</span> Share your link. Anyone who signs up through it becomes your referral.</li>
          <li className="flex gap-3"><span className="font-mono text-primary font-bold">02</span> When they upgrade to Pro, you get <b className="text-emerald-400">1 free month</b> credited automatically.</li>
          <li className="flex gap-3"><span className="font-mono text-primary font-bold">03</span> Hit milestones for bonuses: 5 = +2mo, 10 = +5mo, 25 = +12mo.</li>
          <li className="flex gap-3"><span className="font-mono text-primary font-bold">04</span> Your credits <b>auto-apply as a discount</b> when you upgrade to Pro at checkout.</li>
        </ol>
      </div>

      {/* Leaderboard */}
      <div className="card-flat p-6 mb-8" data-testid="referral-leaderboard">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={16} className="text-amber-400"/>
          <div className="font-display font-bold text-xl">Global leaderboard</div>
        </div>
        {board.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-6">No referrals converted yet — you could be #1.</div>
        ) : (
          <div className="space-y-2">
            {board.map((r, i) => (
              <div key={r.user_id} className={`flex items-center justify-between border-b border-border py-2.5 ${i < 3 ? "bg-primary/5" : ""}`} data-testid={`leaderboard-${i}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 flex items-center justify-center font-display font-black text-sm ${i === 0 ? "bg-amber-500 text-black" : i === 1 ? "bg-zinc-300 text-black" : i === 2 ? "bg-orange-400 text-black" : "bg-accent text-muted-foreground"}`}>{i+1}</div>
                  <div>
                    <div className="font-display font-bold text-sm">{r.name || r.masked_email}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{r.masked_email}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-sm">{r.referrals} <span className="text-muted-foreground font-normal">refs</span></div>
                  <div className="text-[10px] text-emerald-400 font-mono">+{r.credits_earned}mo earned</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-flat p-6" data-testid="referral-list">
        <div className="font-display font-bold text-lg mb-4">Your referrals</div>
        {data.referrals.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-6">No referrals yet — share your link above.</div>
        ) : (
          <div className="space-y-2">
            {data.referrals.map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-border py-3" data-testid={`referral-${i}`}>
                <div>
                  <div className="font-display font-bold text-sm">{r.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{r.email}</div>
                </div>
                <div className={`text-xs px-2 py-1 font-mono uppercase tracking-widest ${r.tier === "pro" ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300" : "bg-zinc-500/10 border border-zinc-500/30 text-muted-foreground"}`}>
                  {r.tier === "pro" ? "PRO · earned +1mo" : "Free"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, icon: Icon }) {
  return (
    <div className="bg-background p-6">
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3"><Icon size={12}/> {label}</div>
      <div className={`font-display font-black text-4xl tracking-tight ${accent && value > 0 ? "text-emerald-400" : ""}`}>{value}</div>
    </div>
  );
}
